import { useState, useEffect } from "react";
import { HelpCircle, Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function StuckButton({ defaultSubject = "", defaultTopic = "" }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(defaultSubject);
  const [topic, setTopic] = useState(defaultTopic);
  const [question, setQuestion] = useState("");
  const [hint, setHint] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("open-stuck", h);
    return () => window.removeEventListener("open-stuck", h);
  }, []);

  const ask = async () => {
    if (!question.trim()) return toast.error("Type what you're stuck on first.");
    setLoading(true); setHint("");
    try {
      const { data } = await api.post("/tutor/stuck", { subject: subject || "general", topic: topic || "general", question });
      setHint(data.hint);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tutor unavailable — try again soon.");
    } finally { setLoading(false); }
  };

  return (
    <>
      <button data-testid="im-stuck-btn"
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-4 md:bottom-8 md:right-8 z-40 flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-5 py-3 rounded-full shadow-lg shadow-amber-500/30 transition-colors">
        <HelpCircle size={18} strokeWidth={2.5} /> I'm stuck
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="stuck-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Let's unstick this together</DialogTitle>
            <DialogDescription>No answers given — just tiny steps and one guiding question.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Subject</Label><Input data-testid="stuck-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Math, Reading…" /></div>
              <div><Label>Topic</Label><Input data-testid="stuck-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Fractions, Thesis…" /></div>
            </div>
            <div>
              <Label>What's confusing you?</Label>
              <Textarea data-testid="stuck-question" rows={4} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="I'm stuck on…" />
            </div>
            <Button data-testid="stuck-ask" onClick={ask} disabled={loading} className="w-full rounded-full bg-teal-700 hover:bg-teal-800">
              {loading ? <><Loader2 className="animate-spin mr-2" size={16} /> Thinking…</> : "Ask for a hint"}
            </Button>
            {hint && (
              <div data-testid="stuck-hint" className="mt-3 p-4 rounded-2xl bg-sky-50 border border-sky-100 text-slate-800 whitespace-pre-wrap text-sm leading-relaxed">
                {hint}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
